import * as React from 'react';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Avatar, Button, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCheckins, checkinEvent } from '../../controllers/checkin/checkin';
import { getAccount } from '../../controllers/accounts/accounts';
import { UserContext } from '../../context/UserContext';
import { InPersonBadge } from '../InPersonBadge';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
}));

export default function CheckInModal({ eventId, open, setOpen, onCheckedIn }) {
  const { userDetails } = React.useContext(UserContext);
  const token = userDetails?.token ?? '';
  const [hiddenFrom, setHiddenFrom] = React.useState([]);
  const [accounts, setAccounts] = React.useState([]);

  const { data, isLoading } = useQuery({
    queryKey: ['getCheckins', eventId, token],
    queryFn: () => getCheckins(token, eventId),
    enabled: open && !!token && !!eventId,
  });

  const checkedIn = (data?.checked_in ?? []).filter(c => c.user !== userDetails?.id);

  React.useEffect(() => {
    if (!open) { setHiddenFrom([]); return }
    let cancelled = false;
    const load = async () => {
      const resolved = await Promise.all(checkedIn.map(async (c) => {
        const account = await getAccount(c.user, token);
        return account?.id ? { ...account, is_in_person: c.is_in_person } : null;
      }));
      if (!cancelled) setAccounts(resolved.filter(Boolean));
    };
    if (checkedIn.length > 0) load(); else setAccounts([]);
    return () => { cancelled = true };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data]);

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: checkinEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getCheckins', eventId] });
      setOpen(false);
      onCheckedIn?.();
    },
  });

  const toggleHidden = (id) => {
    setHiddenFrom((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleClose = () => setOpen(false);

  const handleCheckIn = () => {
    mutation.mutate({ token, id: eventId, user: userDetails?.id, hidden_from: hiddenFrom });
  };

  return (
    <BootstrapDialog onClose={handleClose} open={open} fullWidth maxWidth="xs">
      <DialogTitle sx={{ m: 0, p: 2 }}>Check In</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose anyone already here you'd rather not be alerted about your arrival, then check in.
        </Typography>
        {isLoading ? (
          <CircularProgress size={22} />
        ) : accounts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No one's checked in yet — be the first!</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell align="right">Hide from them</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((account) => (
                <TableRow key={account.id}>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Avatar src={account.avatar} sx={{ width: 28, height: 28 }} />
                      @{account.username}
                      <InPersonBadge isInPerson={account.is_in_person} />
                    </div>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant={hiddenFrom.includes(account.id) ? 'contained' : 'outlined'}
                      color={hiddenFrom.includes(account.id) ? 'secondary' : 'inherit'}
                      onClick={() => toggleHidden(account.id)}
                    >
                      {hiddenFrom.includes(account.id) ? 'Hidden' : 'Hide'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCheckIn} disabled={mutation.isPending} variant="contained" fullWidth>
          {mutation.isPending ? 'Checking in…' : 'Check In'}
        </Button>
      </DialogActions>
    </BootstrapDialog>
  );
}
